import { useEffect, useMemo, useState } from "react";
import client, { resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Trophy, Crosshair, Activity, Globe2, UserCheck, Medal, Award, Star,
} from "lucide-react";

/**
 * Operation Atlas — leaderboards + community recon network stats.
 * Sits below the globe on /wiki/world.
 */
export default function ArcadeLeaderboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("weekly");
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [lb, st] = await Promise.all([
        client.get("/arcade/leaderboard", { params: { period: tab, limit: 25 } }),
        client.get("/arcade/stats"),
      ]);
      setRows(lb.data?.rows || []);
      setStats(st.data || null);
    } catch (_) { /* swallow */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    const id = setInterval(() => { if (document.visibilityState === "visible") load(); }, 60_000);
    return () => clearInterval(id);
  /* eslint-disable-next-line */
  }, [tab]);

  // Identify current user's row (if signed in and on the board).
  const myId = user?.user_id || user?.id || user?.email;
  const myIdx = useMemo(
    () => myId ? rows.findIndex(r => r.user_id === myId) : -1,
    [rows, myId]
  );
  const myRow = myIdx >= 0 ? rows[myIdx] : null;

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <section className="mt-6 md:mt-8" data-testid="arcade-section">
      <ReconNetwork stats={stats} />

      <div className="panel corner-frame mt-4 md:mt-6 p-4 md:p-6 relative overflow-hidden" data-testid="arcade-leaderboard">
        <div className="absolute -right-32 -top-32 w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(243,197,82,0.18)_0%,_transparent_70%)] pointer-events-none" />

        <div className="relative flex flex-wrap items-end justify-between gap-3 mb-5 md:mb-6">
          <div>
            <div className="inline-flex items-center gap-2 mb-1">
              <Trophy className="w-3.5 h-3.5 text-[var(--t-gold)]" />
              <span className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-[var(--t-cyan)] uppercase">// Operator Leaderboard</span>
            </div>
            <h3 className="font-display text-lg md:text-xl text-[var(--t-text)] uppercase">Operation Atlas — Standings</h3>
          </div>
          <div className="inline-flex border border-[var(--t-border)] divide-x divide-[var(--t-border)]" data-testid="arcade-period">
            {[{k:"weekly", l:"This Week"},{k:"alltime", l:"All Time"}].map(o => (
              <button
                key={o.k}
                onClick={() => setTab(o.k)}
                className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition ${tab===o.k ? "bg-[var(--t-blue)]/15 text-[var(--t-cyan)]" : "text-[var(--t-text-dim)] hover:text-[var(--t-text)]"}`}
                data-testid={`arcade-period-${o.k}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>

        {/* "YOUR RANK" callout — visible whenever the user has a score on the current board */}
        {myRow && (
          <div
            className="relative mb-5 md:mb-6 border-2 border-[var(--t-cyan)] bg-[rgba(58,160,255,0.08)] p-3 md:p-4 corner-frame"
            style={{ boxShadow: "0 0 18px rgba(58,160,255,0.35), inset 0 0 14px rgba(58,160,255,0.10)" }}
            data-testid="arcade-you-callout"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="font-mono text-[10px] tracking-[0.3em] text-[var(--t-cyan)] uppercase">// Your_Rank</div>
                <div className="font-display text-2xl md:text-3xl text-[var(--t-cyan)]" style={{ textShadow: "0 0 12px rgba(102,230,255,0.55)" }}>
                  #{myIdx + 1}
                </div>
                <div className="hidden md:block w-px h-10 bg-[rgba(102,230,255,0.25)]" />
                <div className="hidden md:block">
                  <div className="font-mono text-[9px] tracking-[0.22em] text-[var(--t-text-muted)] uppercase">Best Score</div>
                  <div className="font-display text-xl text-[var(--t-text)]">{(myRow.score || 0).toLocaleString()}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <YouStat label="ID'd"  value={myRow.correct} />
                <YouStat label="Combo" value={`x${myRow.best_combo}`} />
                <YouStat label="Phase" value={["I","II","III","IV"][(myRow.max_phase||1)-1] || "I"} />
              </div>
            </div>
          </div>
        )}

        {/* Empty / loading / podium + list */}
        {loading ? (
          <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow text-center py-10">
            // Querying network...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10">
            <Crosshair className="w-8 h-8 text-[var(--t-text-muted)] mx-auto mb-3 opacity-60" />
            <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-text-muted)] uppercase">
              // No operators ranked {tab === "weekly" ? "this week" : "yet"}
            </div>
            <div className="font-display text-base md:text-lg text-[var(--t-text)] uppercase mt-2">Be the first.</div>
          </div>
        ) : (
          <>
            {/* PODIUM — equal-height grid, champion slightly raised on md+ */}
            {podium.length > 0 && (
              <div className="grid grid-cols-3 gap-2 md:gap-4 mb-6 md:mb-8 pt-2" data-testid="arcade-podium">
                {/* Order: 2nd, 1st, 3rd for visual podium centering */}
                {[podium[1], podium[0], podium[2]].map((r, i) => {
                  if (!r) return <div key={i} />;
                  const place = i === 0 ? 2 : i === 1 ? 1 : 3;
                  return (
                    <PodiumCard
                      key={r.user_id}
                      row={r}
                      place={place}
                      isMe={r.user_id === myId}
                    />
                  );
                })}
              </div>
            )}

            {/* Rest of the list (4th+) */}
            {rest.length > 0 && (
              <ul className="space-y-1.5" data-testid="arcade-rows">
                {rest.map((r, i) => {
                  const rank = i + 4;
                  const isMe = r.user_id === myId;
                  return (
                    <li
                      key={r.user_id + i}
                      className={`grid grid-cols-[40px_1fr_auto] md:grid-cols-[44px_1fr_auto_auto_auto] gap-2 md:gap-4 items-center px-3 md:px-4 py-2.5 md:py-3 border transition ${isMe ? "bg-[rgba(58,160,255,0.10)] border-[var(--t-cyan)]" : "bg-[var(--t-bg-2)]/40 border-[var(--t-border)] hover:border-[var(--t-blue-glow)]"}`}
                      style={isMe ? { boxShadow: "0 0 14px rgba(58,160,255,0.35)" } : {}}
                      data-testid={`arcade-row-${rank}`}
                    >
                      <span className="font-display text-lg md:text-xl text-[var(--t-text-dim)] tabular-nums">
                        #{rank}
                      </span>
                      <div className="flex items-center gap-2.5 min-w-0">
                        {r.avatar ? (
                          <img src={resolveImageUrl(r.avatar)} alt="" className="w-7 h-7 md:w-8 md:h-8 rounded-full border border-[var(--t-border)] shrink-0 object-cover" />
                        ) : (
                          <span className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-[var(--t-panel-2)] border border-[var(--t-border)] inline-flex items-center justify-center text-xs text-[var(--t-text-muted)] shrink-0">
                            {(r.username || "?").slice(0,1).toUpperCase()}
                          </span>
                        )}
                        <div className="min-w-0">
                          <div className="font-display text-sm md:text-base text-[var(--t-text)] truncate uppercase tracking-[0.02em] leading-tight">
                            {r.username} {isMe && <span className="text-[var(--t-cyan)]">(you)</span>}
                          </div>
                          <div className="md:hidden font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--t-text-muted)] mt-0.5">
                            {r.correct} ID · x{r.best_combo}
                          </div>
                        </div>
                      </div>
                      <span className="hidden md:inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--t-gold)] min-w-[44px] justify-end">
                        ×{r.best_combo}
                      </span>
                      <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.18em] text-[#5fd99a] min-w-[60px] text-right">
                        {r.correct} ID
                      </span>
                      <span className="font-display text-lg md:text-xl text-[var(--t-cyan)] tabular-nums text-right min-w-[60px] md:min-w-[90px]" style={{ textShadow: "0 0 8px rgba(102,230,255,0.4)" }}>
                        {(r.score || 0).toLocaleString()}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {/* Footer hint */}
        {!loading && rows.length > 0 && (
          <div className="mt-5 pt-4 border-t border-dashed border-[var(--t-border)] flex flex-wrap items-center justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--t-text-muted)]">
              // Showing top {rows.length} · refreshes every 60s
            </span>
            {!myRow && user && (
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--t-cyan)]">
                ▶ Play a run to enter the standings
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/* ------------- Podium card ------------- */

const PODIUM_META = {
  1: { color: "#f3c552", glow: "rgba(243,197,82,0.55)", label: "Champion", icon: Trophy },
  2: { color: "#cfd8e6", glow: "rgba(207,216,230,0.45)", label: "2nd",      icon: Medal  },
  3: { color: "#cd7f32", glow: "rgba(205,127,50,0.50)",  label: "3rd",      icon: Award  },
};

function PodiumCard({ row, place, isMe }) {
  const meta = PODIUM_META[place];
  const Icon = meta.icon;
  const isChampion = place === 1;
  return (
    <div
      className={`relative panel px-2 md:px-3 py-3 md:py-4 flex flex-col items-center text-center transition-transform ${isChampion ? "md:-translate-y-2 border-2" : ""}`}
      style={{
        borderColor: meta.color,
        boxShadow: `0 0 18px ${meta.glow}, inset 0 0 14px ${meta.glow.replace(/0\.\d+/, "0.12")}`,
        background: `linear-gradient(180deg, ${meta.glow.replace(/0\.\d+/, "0.15")} 0%, rgba(2,9,17,0.85) 100%)`,
      }}
      data-testid={`arcade-podium-${place}`}
    >
      {/* Rank badge */}
      <div className="flex items-center justify-center gap-1.5 mb-2">
        <Icon className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: meta.color }} />
        <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.22em]" style={{ color: meta.color }}>
          #{place} {meta.label}
        </span>
      </div>

      {/* Avatar */}
      {row.avatar ? (
        <img
          src={row.avatar}
          alt=""
          className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 mb-2`}
          style={{ borderColor: meta.color, boxShadow: `0 0 12px ${meta.glow}` }}
        />
      ) : (
        <span
          className={`w-10 h-10 md:w-12 md:h-12 rounded-full border-2 mb-2 inline-flex items-center justify-center font-display text-base`}
          style={{ borderColor: meta.color, color: meta.color, boxShadow: `0 0 12px ${meta.glow}` }}
        >
          {(row.username || "?").slice(0,1).toUpperCase()}
        </span>
      )}

      {/* Username */}
      <div
        className="font-display text-xs md:text-sm uppercase tracking-[0.04em] truncate w-full leading-tight"
        style={{ color: meta.color, textShadow: `0 0 10px ${meta.glow}` }}
      >
        {row.username}
        {isMe && <span className="text-[var(--t-cyan)] block text-[9px] tracking-[0.2em] mt-0.5">(YOU)</span>}
      </div>

      {/* Score */}
      <div
        className="font-display text-xl md:text-3xl tabular-nums leading-tight mt-1.5"
        style={{ color: meta.color, textShadow: `0 0 10px ${meta.glow}` }}
      >
        {(row.score || 0).toLocaleString()}
      </div>

      {/* Sub stats */}
      <div className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-[var(--t-text-muted)] mt-1">
        {row.correct} ID · x{row.best_combo}
      </div>
    </div>
  );
}

function YouStat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--t-text-muted)]">{label}</div>
      <div className="font-display text-base md:text-lg text-[var(--t-text)]">{value}</div>
    </div>
  );
}

function ReconNetwork({ stats }) {
  return (
    <div className="panel corner-frame p-4 md:p-6 relative overflow-hidden" data-testid="arcade-recon">
      <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.18)_0%,_transparent_70%)] pointer-events-none" />
      <div className="relative flex items-center gap-2 mb-3">
        <Activity className="w-3.5 h-3.5 text-[#39FF14] pulse-glow" />
        <span className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-[#39FF14] uppercase">// Global_Recon_Network</span>
      </div>
      <div className="grid grid-cols-3 gap-3 md:gap-5">
        <StatBig icon={Crosshair} label="Regions Identified"  value={(stats?.regions_identified || 0).toLocaleString()} accent="var(--t-cyan)" />
        <StatBig icon={UserCheck} label="Operators Active"    value={(stats?.operators_active || 0).toLocaleString()}   accent="#39FF14" />
        <StatBig icon={Globe2}    label="World Knowledge"     value={`${stats?.world_knowledge_pct ?? 0}%`}             accent="var(--t-gold)" sub={stats ? `${stats.countries_identified}/${stats.total_countries}` : null} />
      </div>
    </div>
  );
}

function StatBig({ icon: Icon, label, value, accent, sub }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: accent }}>
      <div className="flex items-center gap-1.5 mb-0.5 text-[var(--t-text-muted)]">
        <Icon className="w-3 h-3" />
        <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.22em]">{label}</span>
      </div>
      <div className="font-display text-xl md:text-3xl leading-tight" style={{ color: accent, textShadow: `0 0 14px ${accent}40` }}>
        {value}
      </div>
      {sub && <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--t-text-muted)] mt-1">{sub}</div>}
    </div>
  );
}
